import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Notice } from "../components/Notice";
import { ProviderButton } from "../components/ProviderButtons";
import { authErrorFrom, clearLinking, enabledProviders, requestPasswordReset, signInWith, type SocialProvider } from "../lib/authProviders";
import { authConfigured } from "../lib/env";
import { supabase } from "../lib/supabase";

type Mode = "signin" | "signup" | "forgot";
export type Audience = "client" | "dealer";

const COPY: Record<Audience, { home: string; signin: string; signup: string; forgot: string; lede: string; other: { label: string; to: string } }> = {
  client: {
    home: "/",
    signin: "Sign in",
    signup: "Create your SoundLayer ID",
    forgot: "Reset your password",
    lede: "Your SoundLayer ID keeps every sound you generate in your private library.",
    other: { label: "Dealer? Sign in to the dealer area", to: "/dealer/signin" },
  },
  dealer: {
    home: "/dealer",
    signin: "Dealer sign in",
    signup: "Create your SoundLayer ID",
    forgot: "Reset your password",
    lede: "Sign in with the SoundLayer ID your dealer account was granted to. Client accounts belong on the client sign-in.",
    other: { label: "Client? Sign in to your library", to: "/signin" },
  },
};

export function SignIn({ initialMode = "signin", audience = "client" }: { initialMode?: "signin" | "signup"; audience?: Audience }) {
  const location = useLocation();
  const copy = COPY[audience];
  const from = (location.state as { from?: string } | null)?.from ?? copy.home;
  const params = new URLSearchParams(location.search);
  // Supabase sends a failed confirmation link back here with error
  // parameters (usually in the hash). They win over ?confirmed=1, which is
  // only the address the link was told to return to, not proof it worked.
  const linkError = authErrorFrom(location.search, location.hash);
  const expiredLink = linkError?.code === "otp_expired" || /expired/.test(linkError?.message ?? "");
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [providerBusy, setProviderBusy] = useState<SocialProvider | null>(null);
  const [providers, setProviders] = useState<Set<SocialProvider>>(new Set());
  const [error, setError] = useState<string | null>(
    linkError
      ? expiredLink
        ? "This confirmation link is invalid or has expired. Send a new one below, or sign in if you already confirmed your address."
        : linkError.message
      : null,
  );
  const [info, setInfo] = useState<string | null>(
    // ?confirmed=1 is only the address the link was told to return to, not
    // proof the email was verified (a verified link arrives with a session
    // and never shows this screen). Neutral guidance only.
    !linkError && params.get("confirmed") === "1"
      ? "Sign in to continue. If your email still needs confirmation, open the latest confirmation email."
      : null,
  );
  const [resent, setResent] = useState(false);

  useEffect(() => {
    let active = true;
    enabledProviders().then((set) => active && setProviders(set));
    return () => {
      active = false;
    };
  }, []);

  if (!authConfigured || !supabase) {
    return (
      <section className="stack">
        <h1>Sign in</h1>
        <Notice kind="error" title="Sign-in is not configured on this deployment.">
          The web app needs <span className="mono">VITE_SUPABASE_URL</span> and{" "}
          <span className="mono">VITE_SUPABASE_PUBLISHABLE_KEY</span>. Until they are set there is no
          SoundLayer ID to sign in with.
        </Notice>
      </section>
    );
  }
  const client = supabase;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "forgot") {
        const message = await requestPasswordReset(email, audience);
        if (message) setError(message);
        else setInfo("If a SoundLayer ID exists for that email, a reset link is on its way. Open the newest message from SoundLayer.");
      } else if (mode === "signin") {
        clearLinking();
        const { error } = await client.auth.signInWithPassword({ email, password });
        if (error) setError(error.message);
      } else {
        const { data, error } = await client.auth.signUp({
          email,
          password,
          // Where the confirmation link returns. Must be allowed in the
          // project's Redirect URLs, otherwise Supabase falls back to its
          // Site URL. See docs/AUTH_SETUP.md.
          options: { emailRedirectTo: confirmReturn },
        });
        if (error) setError(error.message);
        else if (!data.session)
          setInfo("Check your email for a message from SoundLayer and confirm your address, then sign in.");
      }
    } finally {
      setBusy(false);
    }
  }

  const confirmReturn = `${window.location.origin}${audience === "dealer" ? "/dealer/signin" : "/signin"}?confirmed=1`;

  async function resend() {
    if (!email) {
      setError("Enter the email you signed up with, then send a new confirmation email.");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const { error } = await client.auth.resend({ type: "signup", email, options: { emailRedirectTo: confirmReturn } });
      if (error) setError(error.message);
      else {
        setResent(true);
        setInfo("A new confirmation email is on its way. Open the newest message from SoundLayer.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function social(provider: SocialProvider) {
    setProviderBusy(provider);
    setError(null);
    setInfo(null);
    const message = await signInWith(provider, from);
    if (message) {
      setError(message);
      setProviderBusy(null);
    }
    // On success the browser is leaving for the provider; keep the busy state.
  }

  const ordered = (["google", "apple"] as SocialProvider[]).filter((p) => providers.has(p));

  return (
    <section className="stack auth" style={{ maxWidth: 420 }}>
      <div>
        <Logo size={56} className="logo-hero" />
        <h1>{mode === "signin" ? copy.signin : mode === "forgot" ? copy.forgot : copy.signup}</h1>
        <p className="lede">
          {mode === "forgot"
            ? "Enter the email for your SoundLayer ID. We will send a link to choose a new password."
            : mode === "signup" && audience === "dealer"
              ? COPY.client.lede
              : copy.lede}
        </p>
      </div>
      <div className="card stack">
        {ordered.length > 0 && mode !== "forgot" && (
          <>
            <div className="stack" style={{ gap: 10 }}>
              {ordered.map((p) => (
                <ProviderButton
                  key={p}
                  provider={p}
                  action="Continue"
                  busy={providerBusy === p}
                  disabled={providerBusy !== null && providerBusy !== p}
                  onClick={() => void social(p)}
                />
              ))}
            </div>
            <div className="divider" role="separator" aria-label="or">
              <span>or</span>
            </div>
          </>
        )}
        <form className="stack" onSubmit={submit} aria-busy={busy}>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {mode !== "forgot" && (
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {mode === "signup" && <span className="hint">At least 8 characters.</span>}
            </div>
          )}
          {error && (
            <Notice kind="error">
              {error}
              {expiredLink && !resent && (
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="btn btn-block" onClick={() => void resend()} disabled={busy}>
                    Send a new confirmation email
                  </button>
                </div>
              )}
            </Notice>
          )}
          {info && <Notice kind="ok">{info}</Notice>}
          <button className="btn btn-primary btn-block" type="submit" disabled={busy || providerBusy !== null}>
            {busy ? "One moment…" : mode === "signin" ? "Sign in with email" : mode === "forgot" ? "Send reset link" : "Create ID"}
          </button>
          {mode === "signin" && (
            <button
              type="button"
              className="btn btn-quiet btn-block"
              onClick={() => {
                setMode("forgot");
                setError(null);
                setInfo(null);
              }}
            >
              Forgot your password?
            </button>
          )}
          <button
            type="button"
            className="btn btn-quiet btn-block"
            onClick={() => {
              setMode(mode === "signin" ? "signup" : "signin");
              setError(null);
              setInfo(null);
            }}
          >
            {mode === "signin" ? "New here? Create a SoundLayer ID" : mode === "forgot" ? "Back to sign in" : "Already have an ID? Sign in"}
          </button>
        </form>
      </div>
      <p className="small muted" style={{ textAlign: "center", margin: 0 }}>
        <Link to={copy.other.to}>{copy.other.label}</Link>
      </p>
    </section>
  );
}
